import { __ } from '@wordpress/i18n';

/**
 * WooCommerce Source Utilities
 *
 * Shared helper functions for handling multi-product WooCommerce sources.
 * Used by SelectSourcesSection.js, FeedSources.js, and other components.
 */

/**
 * Parse source info for multi-product WooCommerce sources
 *
 * @param {Object} source - The source object
 * @return {Object|null} Parsed info object or null if not a multi-product source
 */
export const parseSourceInfo = ( source ) => {
    if ( !source || source.provider !== 'woocommerce' ) {
        return null;
    }

    let info = source.info;

    if ( typeof info === 'string' ) {
        try {
            info = JSON.parse( info );
        } catch ( e ) {
            // Invalid JSON, return null
            return null;
        }
    }

    // Return info for any WooCommerce source (not just multi_product type)
    // This ensures backwards compatibility with older sources
    return info || null;
};

/**
 * Get badge text for multi-product WooCommerce sources
 *
 * @param {Object} source - The source object
 * @return {string|null} Badge text like "5 Products" or null
 */
export const getSourceBadge = ( source ) => {
    const info = parseSourceInfo( source );

    if ( !info || !info.product_count ) {
        return null;
    }

    const count = parseInt( info.product_count, 10 );
    return count + ' ' + ( count === 1 ? __( 'Product', 'sb-customizer' ) : __( 'Products', 'sb-customizer' ) );
};

/**
 * Get products info for multi-product WooCommerce sources
 * Returns only individually selected products (direct_products)
 *
 * @param {Object} source - The source object
 * @return {Array|null} Array of product objects or null
 */
export const getSourceProducts = ( source ) => {
    const info = parseSourceInfo( source );
    return info?.direct_products || null;
};

/**
 * Get categories info for multi-product WooCommerce sources
 *
 * @param {Object} source - The source object
 * @return {Array|null} Array of category objects or null
 */
export const getSourceCategories = ( source ) => {
    const info = parseSourceInfo( source );
    return info?.categories || null;
};

/**
 * Get tags info for multi-product WooCommerce sources
 *
 * @param {Object} source - The source object
 * @return {Array|null} Array of tag objects or null
 */
export const getSourceTags = ( source ) => {
    const info = parseSourceInfo( source );
    return info?.tags || null;
};

/**
 * Check if a source is a multi-product WooCommerce source
 *
 * @param {Object} source - The source object
 * @return {boolean} True if multi-product WooCommerce source
 */
export const isMultiProductSource = ( source ) => {
    return parseSourceInfo( source ) !== null;
};

const WooCommerceSourceUtils = {
    parseSourceInfo,
    getSourceBadge,
    getSourceProducts,
    getSourceCategories,
    getSourceTags,
    isMultiProductSource
};

export default WooCommerceSourceUtils;
